import { Component, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Store, select } from '@ngrx/store';
import { filter, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { Field, EasyTramPlan } from '../fields/field.model';
import { FieldState } from '../fields/fields.states';
import { MapState } from '../map/map.states';
import { ProcessProgressState } from '../process-progress/process-progress.states';
import {
  selectFields,
  selectCurSelectedFields,
} from '../fields/fields.selector';
import {
  selectStepCounter,
} from '../process-progress/process-progress.selector';
import { MapActions } from '../map/map.actions';

const cirrusDefaultConfig = {
  angleMax: 15.0,
  speedMax: 13.0,
  overlap: 100.0,
};
const preceaDefaultConfig = {
  angleMax: 30.0,
  speedMax: 13.0,
  overlap: 50.0,
};

@Component({
    selector: 'app-machine-selection',
    templateUrl: './machine-selection.component.html',
    styleUrls: ['./machine-selection.component.scss'],
    standalone: false
})
export class MachineSelectionComponent implements OnDestroy {
  easyTramPlan: EasyTramPlan | undefined;

  machineDataForm!: FormGroup;
  headlandForm!: FormGroup;
  corelandForm!: FormGroup;

  public processProgressStep$: Observable<number>;

  public fields$: Observable<Field[]>;
  public fieldsCurSelectedSub: Subscription;
  selectedField: Field | undefined;

  selectedMachineConfig: 'cirrus' | 'precea' | 'custom' = 'custom';

  constructor(
    private fb: FormBuilder,
    public translate: TranslateService,
    private store: Store<{
      fields: FieldState, 
      process: ProcessProgressState,
      mapdata: MapState
     }>,
  ) {

    this.machineDataForm = this.fb.group({
      angleMax: 0,
      speedMax: 0,
      overlap: 0,

      workingWidthSeeder: 0,
      workingWidthCareDevice: 0,
      trackWidthCareDevice: 0,
      wheelWidthCareDevice: 0,
    });
    this.headlandForm = this.fb.group({
      headlandType: 'off',
      headlandWidth: new FormControl({ value: 0, disabled: true }, Validators.required),
      withTramlines: false,
    });
    this.corelandForm = this.fb.group({
      showCoverage: false,
      corelandBaseSegment: undefined,
      shiftOffset: new FormControl({ value: 0, disabled: true }, Validators.required),
      degreeOffset: new FormControl({ value: 0, disabled: true }, Validators.required),
    });

    this.fields$ = this.store.select('fields').pipe(select(selectFields));

    // The current process step is used to determine which UI elements for the easy tram configuration should be shown
    this.processProgressStep$ = this.store.select('process').pipe(select(selectStepCounter));

    this.store
      .select('mapdata')
      .pipe(
        // debounceTime(50),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .pipe(filter(mapData => !!mapData))
      .subscribe((mapData) => {
        if (mapData.easyTramPlan && !mapData.easyTramPlan.equals(this.easyTramPlan)) {
          console.log('Received new mapData in machine selection component: ', mapData);
          this.easyTramPlan = mapData.easyTramPlan.clone();

          if (
            this.easyTramPlan.angleMax == cirrusDefaultConfig.angleMax &&
            this.easyTramPlan.speedMax.toFixed(5) == (cirrusDefaultConfig.speedMax / 3.6).toFixed(5) &&
            this.easyTramPlan.overlap == cirrusDefaultConfig.overlap
          ) {
            this.selectedMachineConfig = 'cirrus';
          } else if (
            this.easyTramPlan.angleMax == preceaDefaultConfig.angleMax &&
            this.easyTramPlan.speedMax.toFixed(5) == (preceaDefaultConfig.speedMax / 3.6).toFixed(5) &&
            this.easyTramPlan.overlap == preceaDefaultConfig.overlap
          ) {
            this.selectedMachineConfig = 'precea';
          } else {
            this.selectedMachineConfig = 'custom';
          }

          this.machineDataForm.patchValue({
            angleMax: mapData.easyTramPlan.angleMax,
            speedMax: mapData.easyTramPlan.speedMax * 3.6, // convert from m/s to km/h for the form
            overlap: mapData.easyTramPlan.overlap,

            workingWidthCareDevice: mapData.easyTramPlan.workingWidthCareDevice,
            wheelWidthCareDevice: mapData.easyTramPlan.wheelWidthCareDevice,
            trackWidthCareDevice: mapData.easyTramPlan.trackWidthCareDevice,
          }, { emitEvent: false });

          this.headlandForm.patchValue({
            headlandType: mapData.easyTramPlan.headlandType,
            headlandWidth: mapData.easyTramPlan.headlandWidth,
            withTramlines: mapData.easyTramPlan.withTramlines,
          }, { emitEvent: false });

          if (mapData.easyTramPlan.corelandBaseSegment !== undefined) {
            this.corelandForm.get('shiftOffset')?.enable();
            this.corelandForm.get('degreeOffset')?.enable();
          } else {
            this.corelandForm.get('shiftOffset')?.disable();
            this.corelandForm.get('degreeOffset')?.disable();
          }
          this.corelandForm.patchValue({
            corelandBaseSegment: mapData.easyTramPlan.corelandBaseSegment,
            shiftOffset: mapData.easyTramPlan.shiftOffset,
            degreeOffset: mapData.easyTramPlan.degreeOffset,
          }, { emitEvent: false });
        }
      });

    this.machineDataForm.valueChanges
      .subscribe((value) => {
        if (this.easyTramPlan) {

          if (
            (value.angleMax && value.angleMax !== this.easyTramPlan.angleMax) ||
            (value.speedMax && value.speedMax / 3.6 !== this.easyTramPlan.speedMax) ||
            (value.overlap && value.overlap !== this.easyTramPlan.overlap)
          ) {
            this.selectedMachineConfig = 'custom';
          }

          const updatedEasyTramPlan = this.easyTramPlan.clone();
          updatedEasyTramPlan.angleMax = value.angleMax;
          updatedEasyTramPlan.speedMax = value.speedMax / 3.6; // convert from km/h to m/s
          updatedEasyTramPlan.overlap = value.overlap;
          updatedEasyTramPlan.workingWidthCareDevice = value.workingWidthCareDevice;
          updatedEasyTramPlan.wheelWidthCareDevice = value.wheelWidthCareDevice;
          updatedEasyTramPlan.trackWidthCareDevice = value.trackWidthCareDevice;

          if (updatedEasyTramPlan.workingWidthCareDevice !== undefined && updatedEasyTramPlan.workingWidthCareDevice > 0) {
            updatedEasyTramPlan.headlandWidth = updatedEasyTramPlan.workingWidthCareDevice;
          }


          this.easyTramPlan = updatedEasyTramPlan;
          this.store.dispatch(MapActions.editEasyTramPlan({ easyTramPlan: updatedEasyTramPlan }));
        }
      });

    this.headlandForm.valueChanges
      .subscribe((value) => {
        if (value.headlandType === 'complete') {
          this.headlandForm.get('headlandWidth')?.enable({ emitEvent: false });
        } else {
          this.headlandForm.get('headlandWidth')?.disable({ emitEvent: false });
        }
      });

    this.fieldsCurSelectedSub = this.store
      .select('fields')
      .pipe(select(selectCurSelectedFields))
      .subscribe((newSelectedFields: Field[]) => {
        if (
          newSelectedFields.length > 0 &&
          !newSelectedFields[0].equals(this.selectedField)
        ) {
          // current check for only one selected field at a time
          this.selectedField = newSelectedFields[0];
        }
      });
  }

  ngOnDestroy(): void {
    this.fieldsCurSelectedSub.unsubscribe();
  }

  switchWithTramlines(event) {
    this.headlandForm.get('withTramlines')?.setValue(event.checked);
  }
  
  switchShowCoverage(event) {
    this.corelandForm.get('showCoverage')?.setValue(event.checked);

    if (!this.easyTramPlan) return;
    const updatedEasyTramPlan = this.easyTramPlan.clone();
    updatedEasyTramPlan.showFieldCoverage = event.checked;
    this.easyTramPlan = updatedEasyTramPlan;
    this.store.dispatch(MapActions.editEasyTramPlan({ easyTramPlan: updatedEasyTramPlan }));
  }

  applyMachineDataConfiguration() {
    if (!this.easyTramPlan) return;

    const updatedEasyTramPlan = this.easyTramPlan.clone();
    updatedEasyTramPlan.angleMax = this.machineDataForm.get('angleMax')?.value;
    updatedEasyTramPlan.speedMax = this.machineDataForm.get('speedMax')?.value;
    updatedEasyTramPlan.overlap = this.machineDataForm.get('overlap')?.value;
    updatedEasyTramPlan.workingWidthCareDevice = this.machineDataForm.get('workingWidthCareDevice')?.value;
    updatedEasyTramPlan.wheelWidthCareDevice = this.machineDataForm.get('wheelWidthCareDevice')?.value;
    updatedEasyTramPlan.trackWidthCareDevice = this.machineDataForm.get('trackWidthCareDevice')?.value;

    if (updatedEasyTramPlan.headlandWidth === undefined || updatedEasyTramPlan.headlandWidth === 0) {
      updatedEasyTramPlan.headlandWidth = this.machineDataForm.get('workingWidthCareDevice')?.value;
    }
    
    this.easyTramPlan = updatedEasyTramPlan;
    this.store.dispatch(MapActions.editEasyTramPlan({ easyTramPlan: updatedEasyTramPlan }));
  }

  applyHeadlandConfiguration() {
    if (!this.easyTramPlan) return;

    const updatedEasyTramPlan = this.easyTramPlan.clone();
    updatedEasyTramPlan.headlandType = this.headlandForm.get('headlandType')?.value;
    updatedEasyTramPlan.headlandWidth = this.headlandForm.get('headlandWidth')?.value;
    updatedEasyTramPlan.withTramlines = this.headlandForm.get('withTramlines')?.value;

    this.easyTramPlan = updatedEasyTramPlan;
    this.store.dispatch(MapActions.editEasyTramPlan({ easyTramPlan: updatedEasyTramPlan }));
  }

  applyCorelandConfiguration() {
    if (!this.easyTramPlan) return;

    const updatedEasyTramPlan = this.easyTramPlan.clone();
    updatedEasyTramPlan.corelandBaseSegment = this.corelandForm.get('corelandBaseSegment')?.value;
    updatedEasyTramPlan.shiftOffset = this.corelandForm.get('shiftOffset')?.value;
    updatedEasyTramPlan.degreeOffset = this.corelandForm.get('degreeOffset')?.value;

    this.easyTramPlan = updatedEasyTramPlan;
    this.store.dispatch(MapActions.editEasyTramPlan({ easyTramPlan: updatedEasyTramPlan }));
  }
  
  applyCirrusDefaultConfiguration() {
    this.machineDataForm.get('angleMax')?.setValue(cirrusDefaultConfig.angleMax);
    this.machineDataForm.get('speedMax')?.setValue(cirrusDefaultConfig.speedMax);
    this.machineDataForm.get('overlap')?.setValue(cirrusDefaultConfig.overlap);

    this.selectedMachineConfig = 'cirrus';
  }

  applyPreceaDefaultConfiguration() {
    this.machineDataForm.get('angleMax')?.setValue(preceaDefaultConfig.angleMax);
    this.machineDataForm.get('speedMax')?.setValue(preceaDefaultConfig.speedMax);
    this.machineDataForm.get('overlap')?.setValue(preceaDefaultConfig.overlap);

    this.selectedMachineConfig = 'precea';
  }
}
